import { ReactNode } from "react";

export enum SUBSCRIPTION_PLAN {
  NO_SUBSCRIPTION_PLAN,
  LEGACY_BASIC_PLAN,
  LEGACY_GOLD_PLAN,
  LEGACY_GOLD_YEARLY_PLAN,
  LEGACY_PLATINUM_PLAN,
  LEGACY_PLATINUM_YEARLY_PLAN,
  LEGACY_DIAMOND_PLAN,
  LEGACY_GOLD_QUARTERLY_PLAN,
  ESSENTIALS_PLAN,
  PLATINUM_PLAN_YEARLY,
  PLATINUM_PLAN_QUARTERLY,
  PLATINUM_PLAN_MONTHLY,
  ARTIST_ESSENTIALS_PLAN,
  ARTIST_PLATINUM_PLAN_YEARLY,
}
export const artistSubscriptionPlans = [
  SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN,
  SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY,
];

export const serviceProviderSubscriptionPlans = [
  SUBSCRIPTION_PLAN.ESSENTIALS_PLAN,
  SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY,
  SUBSCRIPTION_PLAN.PLATINUM_PLAN_QUARTERLY,
  SUBSCRIPTION_PLAN.PLATINUM_PLAN_MONTHLY,
];

export enum SUBSCRIPTION_STATUS {
  INACTIVE = "inactive",
  ACTIVE = "active",
  NOT_DEFINED = "not_defined",
  TRIALING = "trialing",
}

export interface Subscription {
  subscription_plan_choice: SUBSCRIPTION_PLAN;
  stripe_subscription_status: SUBSCRIPTION_STATUS;
}

export interface SubscriptionFaq {
  question: string;
  answer: ReactNode;
}

interface SubscriptionFeature {
  text?: string;
  subtext?: string;
  icon?: boolean;
  disabledColor: boolean;
}

export const SUBSCRIPTION_FEE_MAP = new Map<SUBSCRIPTION_PLAN, number>([
  [SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN, 0.1],
  [SUBSCRIPTION_PLAN.LEGACY_BASIC_PLAN, 0.1],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_PLAN, 0.05],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_YEARLY_PLAN, 0.05],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_QUARTERLY_PLAN, 0.05],
  [SUBSCRIPTION_PLAN.LEGACY_PLATINUM_PLAN, 0.0],
  [SUBSCRIPTION_PLAN.LEGACY_PLATINUM_YEARLY_PLAN, 0.0],
  [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN, 0.1],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_MONTHLY, 0.0],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_QUARTERLY, 0.0],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY, 0.0],
]);

export const SUBSCRIPTION_FEE_STRING_MAP = new Map<SUBSCRIPTION_PLAN, string>([
  [SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN, "10%"],
  [SUBSCRIPTION_PLAN.LEGACY_BASIC_PLAN, "10%"],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_PLAN, "5%"],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_YEARLY_PLAN, "5%"],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_QUARTERLY_PLAN, "5%"],
  [SUBSCRIPTION_PLAN.LEGACY_PLATINUM_PLAN, "0%"],
  [SUBSCRIPTION_PLAN.LEGACY_PLATINUM_YEARLY_PLAN, "0%"],
  [SUBSCRIPTION_PLAN.LEGACY_DIAMOND_PLAN, "0%"],
  [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN, "10%"],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_MONTHLY, "0%"],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_QUARTERLY, "0%"],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY, "0%"],
]);

export const NUM_OFFERABLE_SERVICES = new Map<SUBSCRIPTION_PLAN, string>([
  [SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN, "None"],
  [SUBSCRIPTION_PLAN.LEGACY_BASIC_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_YEARLY_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.LEGACY_GOLD_QUARTERLY_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.LEGACY_PLATINUM_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.LEGACY_PLATINUM_YEARLY_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.LEGACY_DIAMOND_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN, "Unlimited"],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_MONTHLY, " Unlimited"],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_QUARTERLY, "Unlimited"],
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY, "Unlimited"],
]);

export const MembershipFaqList: SubscriptionFaq[] = [
  {
    question: "Do I need to sign up to be on the free Essentials plan?",
    answer:
      "Yes, you’ll need to sign up to create a free account to use EngineEars on the Essentials Membership plan.",
  },
  {
    question: "Is there a free trial for the paid Membership plan?",
    answer:
      "Yes, you can try our Platinum Membership plan for free for 14 days at no risk to you. Experience all the benefits of a Platinum Membership before you decide on what plan is best for your needs.",
  },
  {
    question: "What happens when my free trial period ends?",
    answer:
      "At the end of your 14-day free trial period, your payment method on file will be automatically charged for your selected plan. If you decide you want to change your membership plan, you can do so from your account Settings page at any time during or after your free trial.",
  },
  {
    question: "What payment methods are accepted?",
    answer:
      "We currently accept all major credit cards and debit cards. You can be billed monthly, but save 17% if you pay quarterly, or 50% if you pay annually.",
  },
  {
    question: "Which EngineEars Membership plan is best for me?",
    answer:
      "The choice is completely up to you. Compared to the Platinum monthly plan, the Platinum annual plan saves you 50%, and the Platinum quarterly plan saves you 17%. At $30 USD only, you can get the monthly plan and experience all the benefits of a paid Membership subscription before deciding on a longer-term commitment.",
  },
  {
    question:
      "Do I have to renew my EngineEars Membership at the end of each period?",
    answer:
      "Paid subscriptions will automatically renew for the same subscription period (auto-renews for the next month, quarter, or year) unless you downgrade your plan before your renewal date.",
  },
  {
    question:
      "How can I upgrade, downgrade, or change my EngineEars Membership plan?",
    answer:
      "Visit your account Settings and go to the Payment & Billing tab. From there, you can click the Manage Subscription button to make changes to your current Membership plan. In addition to changing the type of Membership plan (e.g. from Essentials to Platinum), you can also make changes to the subscription renewal period (e.g. from monthly to annual, or quarterly to monthly).",
  },
  {
    question: "Can I cancel my EngineEars Membership plan at any time?",
    answer:
      "Yes, you can cancel at any time. Visit your account Settings and goto the Payment & Billing tab. From there, you can click the Manage Subscription button to cancel your current paid Membership plan. If canceled before the next renewal date, you will not be automatically billed for the next renewal period.",
  },
  {
    question:
      "I was on a legacy Membership plan, what happens to my Membership subscription now?",
    answer:
      "The legacy Basic plan is automatically converted to the Essentials plan and will remain free. The legacy Gold/Diamond/Platinum paid plans are automatically converted to the new Platinum Membership plan. You will not be charged any more per subscription period than you were paying under a legacy paid plan, however, any cost savings will be passed on to you!",
  },
  {
    question:
      "Do I need an annual Platinum Membership plan in order to access training courses and events?",
    answer:
      "Yes. Only Platinum Members on the annual subscription plan will have access to exclusive Community Events and EngineEars School of Music training/certification courses such as Dolby Atmos. If your account is on a Platinum Membership but billed monthly or quarterly, you will need to upgrade to an annual billing subscription to gain access.",
  },
  {
    question: "Do I need an account with Muso.AI to have verified credits?",
    answer:
      "Yes, if you have an existing account with Muso.ai you can connect it with your EngineEars account by logging in and linking it. If you do not have an account with Muso.ai you can create one by claiming your Muso.ai profile first, and then linking it afterward from your EngineEars account.",
  },
];

interface DisplayAttributes {
  text?: string;
  subtext?: string;
  displayIcon?: boolean;
  disabledColor: boolean;
}

export interface BulletPointWithComparison {
  feature: string;
  comparison: Record<number, DisplayAttributes>;
}

enum Phrase {
  Available = "Available",
  ComingSoon = "Available (coming soon)",
  Included = "Included",
  NotAvailable = "Not available",
  NotIncluded = "Not included",
}

const NotIncludedBlock = {
  subtext: Phrase.NotIncluded,
  displayIcon: true,
  disabledColor: true,
};

const IncludedBlock = {
  subtext: Phrase.Included,
  displayIcon: true,
  disabledColor: false,
};

const AvailableBlock = {
  subtext: Phrase.Available,
  displayIcon: true,
  disabledColor: false,
};

const NotAvailableBlock = {
  subtext: Phrase.NotAvailable,
  displayIcon: true,
  disabledColor: true,
};

const ComingSoonBlock = {
  subtext: Phrase.ComingSoon,
  displayIcon: true,
  disabledColor: false,
};

export const subscriptionPlanToTextMapping: Record<
  number,
  { title: string; subtitle: string; subscriptionCardSubtitle: string }
> = {
  [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
    title: "Essentials",
    subtitle: "No credit card required",
    subscriptionCardSubtitle: "No upfront cost",
  },
  [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
    title: "Artist Essentials",
    subtitle: "No credit card required",
    subscriptionCardSubtitle: "No upfront cost",
  },
  [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
    title: "Platinum",
    subtitle: "Starting at",
    subscriptionCardSubtitle: "14-day free trial",
  },
  [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
    title: "Artist Platinum",
    subtitle: "annual membership",
    subscriptionCardSubtitle: "Keep 100% of earnings",
  },
};

export enum ComparisonSectionTitles {
  CoreFeatures = "Core Features",
  EmpowerMyBusiness = "Empower my business",
  StreamlineMyBusiness = "Streamline my business",
  MonetizeMyBusiness = "Monetize my business",
  PromoteMyBusiness = "Promote my business",
  LevelUpMyBusiness = "Level up my business",
  KeyDifferences = "Key Differences",
  GrowYourEarnings = "Grow your earnings",
  SellDirectlyToFans = "Sell Directly To Fans",
  DistributeYourMusic = "Distribute Your Music",
  BuildAndPromoteYourBrand = "Build And Promote Your Brand",
  Support = "Support",
}

export const sectionsWithComparisons = new Map<
  ComparisonSectionTitles,
  BulletPointWithComparison[]
>([
  [
    ComparisonSectionTitles.CoreFeatures,
    [
      {
        feature:
          "Custom Profile with share link (showcase services offered, awards, credits, achievements plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Centralized Booking Management (integrated calendar for automated scheduling, reminders plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Project Workflow Management (tools to track progress, timing, and deliverables)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Streamlined Bookkeeping (automated invoices + receipts, transaction history, payment tracking, file exports plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.KeyDifferences,
    [
      {
        feature: "Service Fee",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            text: "10%",
            subtext: "per booking transaction",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            text: "0%",
            subtext: "per booking transaction",
            disabledColor: false,
          },
        },
      },
      {
        feature: "Fund withdrawals to linked bank",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            text: "48 hours",
            subtext: "3% fee for instant payout upon project completion",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            text: "Instant",
            subtext: "Upon project completion",
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Instant payout for completed Record Label projects (net-zero payment terms)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            text: "+3%",
            subtext: "additional fee",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            text: "+0% fee",
            subtext: "no additional fees",
            disabledColor: false,
          },
        },
      },
      {
        feature: "Highlight your verified music credits powered by Muso.ai",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: Phrase.NotIncluded,
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Exclusive access to Community Events",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: Phrase.NotIncluded,
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            subtext: "Included for Annual Members only",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Training + Certification Courses by EngineEars School of Music (Dolby Atmos plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: Phrase.NotIncluded,
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            subtext: "Included for Annual Members only",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Exclusive access to Tutorials + Demo videos (mixing/mastering techniques, plug-ins plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: Phrase.NotIncluded,
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Exclusive access to Content and Live Streams",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: Phrase.NotIncluded,
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.EmpowerMyBusiness,
    [
      {
        feature: "Service Fee",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            text: "10%",
            subtext: "per booking transaction",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            text: "0%",
            subtext: "per booking transaction",
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Booking Limit (number of projects or sessions that can be booked)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            text: "Unlimited",
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            text: "Unlimited",
            disabledColor: false,
          },
        },
      },
      {
        feature: "Customizable service rate cards and packages",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Custom service bundles (coming soon)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Host Studio Rooms for recording session bookings",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: "1 included",
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            subtext: "2 included (additional rooms at $60/year)",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Easy and flexible payment options for clientele",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.StreamlineMyBusiness,
    [
      {
        feature:
          "Team Management (invite teammates, affiliates, collaborators plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Shareable Project links (invite and share project access to collaborators)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Secure File Storage for Projects",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: "1TB included",
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            subtext: "Unlimited",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Secure real-time Messaging for ease of collaboration",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "A/B Audio Player with lossless playback to compare versions",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Feedback Management (consolidate shared feedback and time-stamped comments per track to streamline edits)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Help Center access",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "24-hour Customer support",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.MonetizeMyBusiness,
    [
      {
        feature: "Fund withdrawals to linked bank",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            text: "48 hours",
            subtext: "3% fee for instant payout upon project completion",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            text: "Instant",
            subtext: "Upon project completion",
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Instant payout for completed Record Label projects (net-zero payment terms)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            text: "+3%",
            subtext: "additional fee",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            text: "+0% fee",
            subtext: "no additional fees",
            disabledColor: false,
          },
        },
      },
      {
        feature: "Consolidated tax documents",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Project dispute mediation + Fraud prevention",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.PromoteMyBusiness,
    [
      {
        feature:
          "Consideration for EngineEars Pro: increased opportunities with exclusive exposure to Record Labels",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Profile Visitor Analytics",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            subtext: "See number of profile views",
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            subtext: "See who’s visited your profile (coming soon)",
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Portfolio with A/B audio player to show off your featured work",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Custom promo codes + social media assets for self-promotion",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Highlight your verified music credits powered by Muso.ai",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: NotIncludedBlock,
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.LevelUpMyBusiness,
    [
      {
        feature: "Exclusive access to Community Events",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: "Not included",
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            subtext: "Included for Annual Members only",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Training + Certification Courses by EngineEars School of Music (Dolby Atmos plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: "Not included",
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            subtext: "Included for Annual Members only",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Exclusive access to Tutorials + Demo videos (mixing/mastering techniques, plug-ins plus more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: "Not included",
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Exclusive access to Content and Live Streams",
        comparison: {
          [SUBSCRIPTION_PLAN.ESSENTIALS_PLAN]: {
            subtext: "Not included",
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.PLATINUM_PLAN_YEARLY]: {
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
]);

export const artistSubscriptionPlansComparison = new Map<
  string,
  BulletPointWithComparison[]
>([
  [
    ComparisonSectionTitles.GrowYourEarnings,
    [
      {
        feature: "Service Fee",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
            text: "10%",
            subtext: "keep 90% of your earnings",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            text: "0%",
            subtext: "keep 100% of your earnings",
            disabledColor: false,
          },
        },
      },
      {
        feature: "Payout frequency",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
            text: "Daily",
            subtext: "last week's sales paid out daily",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            text: "Daily",
            subtext: "last week's sales paid out daily",
            disabledColor: false,
          },
        },
      },
      {
        feature: "Withdraw earnings to linked bank",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
            text: "7 days",
            subtext: "after sales",
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            text: "7 days",
            subtext: "after sales",
            disabledColor: false,
          },
        },
      },
      {
        feature: "Streaming royalties",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
            subtext: "Not available",
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            subtext: "Available (coming soon)",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Royalty payment splits with collaborators",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
            subtext: "Not available",
            displayIcon: true,
            disabledColor: true,
          },
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            subtext: "Available (coming soon)",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature: "Consolidated tax documents and full transaction history",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
            subtext: "Included",
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            subtext: "Included",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
      {
        feature:
          "Analytics (on profile visits, sales performance, campaign performance, streaming stats, and more)",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: {
            subtext: "Included",
            displayIcon: true,
            disabledColor: false,
          },
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            subtext: "Included",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.SellDirectlyToFans,
    [
      {
        feature: "Sell Digital Music",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "Sell exclusive content and VIP experiences",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: ComingSoonBlock,
        },
      },
      {
        feature: "Sell merchandise and physical goods",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: ComingSoonBlock,
        },
      },
      {
        feature: "Customizable prices",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "Allow flexible pricing (fans can pay what they want)",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "Offer discounts and promo codes",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "Manage permissions and access after purchase",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.DistributeYourMusic,
    [
      {
        feature: "Unlimited Releases",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotIncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature:
          "Distribute your music to streaming platforms and digital stores",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            ...ComingSoonBlock,
            text: "Upload to 100+ streaming platforms and stores like Spotify, Apple Music, YouTube, Amazon, Instagram, Facebook, TikTok, Tidal, Deezer and many more",
          },
        },
      },
      {
        feature: "Reach global audiences",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            ...ComingSoonBlock,
            text: "100+ countries around the world",
          },
        },
      },
      {
        feature: "Customize release dates/times",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: AvailableBlock,
        },
      },
      {
        feature: "Setup pre-orders",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: AvailableBlock,
        },
      },
      {
        feature: "Release submission time",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: NotAvailableBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            subtext: "1-3 business days",
            displayIcon: true,
            disabledColor: false,
          },
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.BuildAndPromoteYourBrand,
    [
      {
        feature: "Customizable artist profile",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "Shareable link",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "Automated promo assets and tools to announce releases",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature:
          "Social ad campaigns to reach new audiences (powered by SymphonyOS)",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            ...IncludedBlock,
            text: "(includes $5 ad credit)",
          },
        },
      },
      {
        feature:
          "Secured messaging to engage directly with fans and supporters",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
    ],
  ],
  [
    ComparisonSectionTitles.Support,
    [
      {
        feature:
          "Hire verified audio engineers and recording studios on EngineEars to make your next record",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            ...IncludedBlock,
            text: "(includes 30% discount)",
          },
        },
      },
      {
        feature: "Access to creative community",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "Help Center access",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: IncludedBlock,
        },
      },
      {
        feature: "24-hour customer support",
        comparison: {
          [SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN]: IncludedBlock,
          [SUBSCRIPTION_PLAN.ARTIST_PLATINUM_PLAN_YEARLY]: {
            ...IncludedBlock,
            text: "(Priority Support)",
          },
        },
      },
    ],
  ],
]);
